import { Link } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { OPERATIONS, type OperationId } from '@visualitics-toolkit/shared';

import { Badge, EmptyState, Select } from '../components/atoms';
import { DurationPair, LoadingBlock, formatDateTime } from '../components/molecules';
import { useActiveConnection, useRun, useRuns } from '../lib/queries';
import { runStatusTone } from './HomePage';

export function RunsPage(): ReactNode {
  const connection = useActiveConnection();
  const [operationId, setOperationId] = useState<string>('');
  const runs = useRuns({ connectionId: connection?.id, operationId: operationId || undefined, limit: 100 });

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <h1 className="text-lg font-semibold text-content">Histórico de runs</h1>
        <div className="w-72">
          <Select value={operationId} onChange={(event) => setOperationId(event.target.value)}>
            <option value="">Todas as operações</option>
            {(Object.keys(OPERATIONS) as OperationId[]).map((id) => (
              <option key={id} value={id}>
                {OPERATIONS[id].title}
              </option>
            ))}
          </Select>
        </div>
      </header>

      {runs.isLoading && <LoadingBlock />}
      {runs.data?.length === 0 && (
        <EmptyState title="Nenhuma execução registrada" description="Toda tentativa vira um run — inclusive as recusadas." />
      )}

      {runs.data && runs.data.length > 0 && (
        <div className="overflow-hidden rounded-md border border-border">
          <table className="w-full text-left text-sm">
            <thead className="bg-surface-raised text-xs uppercase text-muted">
              <tr>
                <th className="px-3 py-2">Quando</th>
                <th className="px-3 py-2">Operação</th>
                <th className="px-3 py-2">Ambiente</th>
                <th className="px-3 py-2">Operador</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Tempo</th>
              </tr>
            </thead>
            <tbody>
              {runs.data.map((run) => (
                <tr key={run.id} className="border-t border-border/60">
                  <td className="px-3 py-1.5 text-xs text-muted">{formatDateTime(run.createdAt)}</td>
                  <td className="px-3 py-1.5">
                    <Link to="/runs/$runId" params={{ runId: run.id }} className="hover:text-accent">
                      {OPERATIONS[run.operationId]?.title ?? run.operationId}
                    </Link>
                  </td>
                  <td className="px-3 py-1.5 text-xs">{run.environment}</td>
                  <td className="px-3 py-1.5 text-xs text-muted">{run.userEmail ?? '—'}</td>
                  <td className="px-3 py-1.5">
                    <Badge tone={runStatusTone(run.status)}>{run.status}</Badge>
                    {run.errorCode && (
                      <span className="ml-2 font-mono text-[11px] text-muted">{run.errorCode}</span>
                    )}
                  </td>
                  <td className="px-3 py-1.5">
                    <DurationPair estimatedMs={run.estimatedDurationMs} actualMs={run.executeMs} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function RunDetailPage({ runId }: { runId: string }): ReactNode {
  const run = useRun(runId);

  if (run.isLoading) return <LoadingBlock />;
  if (!run.data) return <EmptyState title="Run não encontrado" />;

  const detail = run.data;

  return (
    <div className="space-y-4">
      <header className="space-y-1">
        <Link to="/runs" className="text-xs text-accent underline">
          ← voltar ao histórico
        </Link>
        <h1 className="text-lg font-semibold text-content">
          {OPERATIONS[detail.operationId]?.title ?? detail.operationId}
        </h1>
        <p className="flex flex-wrap items-center gap-2 text-sm text-muted">
          <Badge tone={runStatusTone(detail.status)}>{detail.status}</Badge>
          <span>{formatDateTime(detail.createdAt)}</span>
          <span>· {detail.environment}</span>
          <span>· {detail.connectionName ?? detail.connectionId}</span>
          <span>· {detail.userEmail ?? '—'}</span>
          <DurationPair estimatedMs={detail.estimatedDurationMs} actualMs={detail.executeMs} />
        </p>
      </header>

      {detail.errorMessage && (
        <div className="card border-severity-irreversible/60">
          <p className="font-mono text-xs text-severity-irreversible">
            {detail.errorCode}: {detail.errorMessage}
          </p>
        </div>
      )}

      {detail.artifacts.length > 0 && (
        <section className="card space-y-2">
          <h2 className="text-sm font-medium text-content">Artefatos</h2>
          {detail.artifacts.map((artifact) => (
            <a
              key={artifact.id}
              className="block font-mono text-xs text-accent underline"
              href={`/api/v1/artifacts/${artifact.id}/download`}
            >
              {artifact.filename}
            </a>
          ))}
        </section>
      )}

      <JsonSection title="Parâmetros (redigidos)" data={detail.params} />
      <JsonSection title="Impacto aceito no momento da execução" data={detail.impactSnapshot} />
      <JsonSection title="Confirmação" data={detail.confirmation} />
      <JsonSection title="Preflight" data={detail.preflight} />
      <JsonSection title="Plano" data={detail.plan} />
      <JsonSection title="Contagens após execução" data={detail.rowCountsAfter} />
    </div>
  );
}

function JsonSection({ title, data }: { title: string; data: unknown }): ReactNode {
  if (data === null || data === undefined) return null;
  return (
    <details className="card">
      <summary className="cursor-pointer text-sm font-medium text-content">{title}</summary>
      <pre className="mt-2 max-h-80 overflow-auto font-mono text-xs text-muted">
        {JSON.stringify(data, null, 2)}
      </pre>
    </details>
  );
}
